import { z } from 'zod';
import { NumberFormat, ColourToken, Spacing } from '../../artifacts/presentation-values.mjs';
import { requireCondition } from './errors.mjs';
import { ImageFit, ImageRatio } from '../../artifacts/images/values.mjs';
import {
  ImageFitPresentation,
  ImageRatioPresentation,
  imagePresentationEditable,
} from './image-presentation.mjs';

const tokenKeys = z
  .array(ColourToken)
  .max(256)
  .refine((keys) => new Set(keys).size === keys.length, 'duplicate_slot_token')
  .refine((keys) => !keys.some(isAbsenceValue), 'reserved_absence_value');
const common = { presence: z.enum(['present', 'absent']) };
export const SlotPresentation = z
  .discriminatedUnion('kind', [
    z.strictObject({
      ...common,
      kind: z.literal('colour'),
      legacy: z.literal('none'),
      effectiveDefault: ColourToken.refine(
        (value) => value !== 'scalar-native',
        'invalid_colour_default',
      ),
      channel: z.enum(['text', 'background', 'border', 'fill', 'stroke']),
      allowedColourTokens: tokenKeys.min(1),
    }),
    z.strictObject({
      ...common,
      kind: z.literal('spacing'),
      legacy: z.literal('none'),
      effectiveDefault: Spacing,
      side: z.enum([
        'marginTop',
        'marginRight',
        'marginBottom',
        'marginLeft',
        'paddingTop',
        'paddingRight',
        'paddingBottom',
        'paddingLeft',
        'rowGap',
        'columnGap',
      ]),
      allowedSpacingTokens: tokenKeys,
    }),
    z.strictObject({
      ...common,
      kind: z.literal('numberFormat'),
      legacy: z.enum(['none', 'supported', 'unsupported']),
      effectiveDefault: z.union([NumberFormat, z.literal('scalar-native')]),
      valueType: z.enum(['money', 'ratio', 'count', 'number']),
      formatSeed: NumberFormat.optional(),
    }),
    ImageFitPresentation,
    ImageRatioPresentation,
  ])
  .superRefine((metadata, ctx) => {
    if (
      metadata.kind === 'colour' &&
      metadata.effectiveDefault === 'none' &&
      !['background', 'border'].includes(metadata.channel)
    )
      ctx.addIssue({ code: 'custom', message: 'unregistered_absence_default' });
  });

export function isAbsenceValue(value) {
  return value === 'none' || value === 'scalar-native';
}

export function inspectSlotPresentation(slot) {
  requireCondition(slot.presentation, 'slot_presentation_unavailable');
  const metadata = SlotPresentation.parse(slot.presentation);
  requireCondition(metadata.kind === slot.property, 'slot_presentation_mismatch');
  if (metadata.presence === 'absent') {
    requireCondition(
      metadata.legacy === 'none' && sameValue(slot.value, metadata.effectiveDefault),
      'invalid_absent_slot_value',
    );
  } else {
    requireCondition(!isAbsenceValue(slot.value), 'invalid_present_slot_value');
    if (metadata.legacy !== 'unsupported') {
      const schema = {
        colour: ColourToken,
        spacing: Spacing,
        numberFormat: NumberFormat,
        imageFit: ImageFit,
        imageRatio: ImageRatio,
      }[metadata.kind];
      requireCondition(schema.safeParse(slot.value).success, 'invalid_present_slot_value');
    }
  }
  return metadata;
}

function sameValue(value, expected) {
  if (typeof expected !== 'object') return value === expected;
  const parsed = NumberFormat.safeParse(value);
  if (!parsed.success) return false;
  const keys = Object.keys(expected);
  return (
    keys.length === Object.keys(parsed.data).length &&
    keys.every((key) => parsed.data[key] === expected[key])
  );
}

export function slotClearable(slot) {
  return (
    slot.sourceKind === 'artifact-node' &&
    slot.presentation?.presence === 'present' &&
    slot.presentation.kind === slot.property &&
    imagePresentationEditable(slot.presentation) &&
    slot.presentation.legacy !== 'unsupported'
  );
}
